import { toByteArray } from 'base64-js'
import { getAuthToken } from 'services/session'
import { Stream, MessageHandler } from 'types'

export class EventSourceWithAuth {
  private eventSource?: EventSource
  private authToken?: string

  constructor(
    private readonly url: string,
    private readonly messageHandler: MessageHandler<MessageEvent>,
    private readonly errorHandler: MessageHandler<ErrorEvent>,
  ) {
    getAuthToken().then(this.open)
  }

  public close = () => {
    if (this.eventSource) {
      this.eventSource.removeEventListener('message', this.onMessage)
      this.eventSource.removeEventListener('error', this.onError)
      this.eventSource.close()
      this.eventSource = undefined
    }
  }

  private open = (authToken: string) => {
    this.authToken = authToken
    this.eventSource = new EventSource(`${this.url}&id_token=${authToken}`)
    this.eventSource.addEventListener('message', this.onMessage)
    this.eventSource.addEventListener('error', this.onError)
  }

  private onMessage = (e: Event) => {
    this.messageHandler(e as MessageEvent)
  }

  private onError = async (e: Event) => {
    const authToken = await getAuthToken()

    if (this.authToken !== authToken) {
      this.close()
      this.open(authToken)
    } else {
      this.errorHandler(e as ErrorEvent)
    }
  }
}

export const handleStream = <T>(
  url: string,
  handler: { decode(reader: Uint8Array): T },
): Stream<T> => (
  onmessage: MessageHandler<T>,
  onerror: MessageHandler<Error>,
) => {
  const messageHandler = (e: MessageEvent) => { onmessage(handler.decode(toByteArray(e.data))) }
  const errorHandler = (e: ErrorEvent) => { onerror(new Error(e.message)) }

  const es = new EventSourceWithAuth(url, messageHandler, errorHandler)

  return () => { es.close() }
}
